/**
 * Should delete this file and references
 * once https://aviosgroup.atlassian.net/browse/RPS-14800 is delivered
 */
import { useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { Badge, Box, Heading } from '@alto-avios/alto-ui'
import { ContentfulCardLegacy as ContentfulCard } from '@reward-platform/contentful'
import { useAutoCompleteContext } from '@reward-platform/lift/components'
import { usePlatformTheme } from '~/context/theme'
import { getContentfulImageProps, getFallbackImageUrlForTheme } from '~/utils/assets'
import { useTagManagerContext } from '~/context/analytics'
import { usePlatformRoutes } from '~/context/platform'
import ImageCard from '../Card/ImageCard'

import styles from './PopularDestinations.module.scss'

type PopularDestinationsProps = {
  destinations: ContentfulCard[]
  displayInline: boolean
}

/**
 * TODO: - Temporarily handle using new query params until contentful homepage is rebuilt
 * https://aviosgroup.atlassian.net/browse/RPS-14800
 * will be reimplemented as part of https://aviosgroup.atlassian.net/browse/RPS-14868
 */
const getDestinationSearchQuery = (searchString: string): string | undefined => {
  const queryMap: Record<string, string> = {
    london: 'tab=hotels&dest_search=london&dest_type=city&dest_id=2114',
    maldives: 'tab=hotels&dest_search=maldives&dest_type=multi_region&dest_id=109',
    'new york': 'tab=hotels&dest_search=new+york&dest_type=city&dest_id=2621',
    dubai: 'tab=hotels&dest_search=dubai&dest_type=city&dest_id=6053839',
    dublin: 'tab=hotels&dest_search=dublin&dest_type=city&dest_id=1069',
  }
  return queryMap[searchString.toLowerCase()]
}

export default function PopularDestinations(props: PopularDestinationsProps) {
  const { destinations, displayInline } = props
  const { themeId } = usePlatformTheme()
  const { createRoutePath } = usePlatformRoutes()
  const fallbackSrc = useMemo(() => getFallbackImageUrlForTheme(themeId), [themeId])
  const t = useTranslations('pages.details.hotels.labels')
  const { pushEventToDataLayer } = useTagManagerContext()
  const autoCompleteContext = useAutoCompleteContext()

  const handleDestinationClick = useCallback(
    (destinationHeading?: string) => {
      if (!destinationHeading) {
        return
      }

      pushEventToDataLayer('navigation', {
        nav_element: 'hotels trending destinations',
        nav_type: 'cta',
        nav_action: `destination name ${destinationHeading} | popular`,
        referrer: document.referrer,
        departure_date: 'not_applicable',
        arrival_date: 'not_applicable',
        origin: 'not_applicable',
        destination: destinationHeading,
      })

      if (autoCompleteContext?.closeMenu) {
        autoCompleteContext.closeMenu()
      }
    },
    [autoCompleteContext, pushEventToDataLayer]
  )

  return (
    <Box width="100%" flexDirection="column" padding="lg" gap="md" position="relative">
      <Heading as="h4">{t('trending_destinations_title')}</Heading>
      <Box gap="lg" className={displayInline ? styles.inlineContainer : styles.gridContainer}>
        {destinations.map((destination) => {
          const { imgSrc, imgAlt } = getContentfulImageProps({
            bynderImage: destination.bynderImage,
            image: destination.image,
            fallbackAlt: destination.heading,
          })
          const defaultAlt = `popular destination image for ${destination.heading}`
          const hasBynderImage = destination.bynderImage?.some(
            (item) => item.selectedFile?.url || item.src
          )
          const imageAlt = hasBynderImage ? imgAlt : defaultAlt

          return (
            <Link
              key={JSON.stringify(destination)}
              href={{
                pathname: createRoutePath('/'),
                query: destination.heading
                  ? getDestinationSearchQuery(destination.heading)
                  : undefined,
              }}
              scroll={false}
              shallow={false}
              onClick={() => {
                return handleDestinationClick(destination.heading)
              }}
              className={styles.linkContainer}
            >
              <ImageCard
                src={imgSrc || fallbackSrc}
                alt={imageAlt}
                height={200}
                width={300}
                objectFit="cover"
                sizes="(max-width: 650px) 80vw, 300px"
                imagePosition="bottom"
              >
                <ImageCard.Caption
                  heading={destination.heading}
                  headerItem={<Badge styleVariant="brand">{t('popular')}</Badge>}
                />
              </ImageCard>
            </Link>
          )
        })}
      </Box>
    </Box>
  )
}
